<script>
const BADGE_THEME = Object.freeze({
  RED: "red",
  BLUE: "blue",
  YELLOW: "yellow",
  GREEN: "green",
  GRAY: "gray",
});
const PADDING_SIZE = Object.freeze({
  NORMAL: "normal",
  BIG: "big",
});
</script>

<script setup>
import { computed } from "vue";

const emit = defineEmits(["update:model-value"]);
const props = defineProps({
  theme: {
    type: String,
    validator: (prop) => Object.values(BADGE_THEME).includes(prop),
    default: BADGE_THEME.RED,
  },
  paddingSize: {
    type: String,
    validator: (prop) => Object.values(PADDING_SIZE).includes(prop),
    default: PADDING_SIZE.NORMAL,
  },
  label: {
    type: String,
    default: null,
  },
  count: {
    type: [String, Number],
    default: null,
  },
  value: {
    type: [String, Number],
    required: true,
  },
  modelValue: {
    type: [String, Number],
    default: null,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  darker: {
    type: Boolean,
    default: false,
  },
});

const isSelected = computed(() => {
  if (props.selected) return true;
  if (props.modelValue === null && props.value === null) {
    return false;
  }
  return props.modelValue === props.value;
});

const paddingClass = computed(() => {
  return props.paddingSize === "big" ? "padding-big" : "padding-normal";
});

const selectBadge = () => {
  if (isSelected.value) {
    return emit("update:model-value", null);
  }
  emit("update:model-value", props.value);
};
</script>

<template>
  <span
    class="status-badge text-neutral-400"
    :class="{
      'status-badge--selected': isSelected,
      'status-badge--darker': darker,
      [paddingClass]: true,
      [theme]: theme,
    }"
    @click="selectBadge"
  >
    <span class="d-flex align-items-center">
      <IconCircleFill />
      <span class="ms-2 font-size-xs text-neutral-400">
        {{ label }}
        <template v-if="count">({{ count }})</template>
      </span>
    </span>
  </span>
</template>

<style scoped lang="scss">
.status-badge {
  border: 1px solid $bluish-gray-300;
  border-radius: 60px;
  font-size: 0.875rem;
  cursor: pointer;

  &.padding-normal {
    padding: 0.25rem 0.5rem;
  }

  &.padding-big {
    padding: 0.5rem 0.75rem;
  }

  // Refer to design specs since not all variants are available now.
  // @REF: https://www.figma.com/design/BT2vsNW0gWTsZx5IgJ8EAw/EZee-Assist-Final-Handover?node-id=1331-57151&node-type=symbol&m=dev.

  &.status-badge--selected {
    &.red {
      background-color: $red-50;
      border: 1px solid $red-100;
    }

    &.gray {
      background-color: $neutral-150;
      border: 1px solid $neutral-300;
    }

    &.green {
      background-color: $green-50;
      border: 1px solid $green-100;
    }

    &.yellow {
      background-color: $yellow-50;
      border: 1px solid $yellow-100;
    }

    &.blue {
      background-color: var(--primary-50);
      border: 1px solid var(--primary-150);
    }
  }

  &.status-badge--darker {
    &.red {
      background-color: $red-0;
      border: 1px solid $red-100;
    }

    &.gray {
      background-color: $neutral-50;
      border: 1px solid $neutral-200;
    }

    &.green {
      background-color: $green-0;
      border: 1px solid $green-100;
    }
  }

  &.red {
    background-color: $red-0;
    border: 1px solid $red-50;

    svg {
      fill: $red-200;
    }
  }

  &.green {
    background-color: $green-0;
    border: 1px solid $green-50;

    svg {
      fill: $green-150;
    }
  }

  &.yellow {
    background-color: $yellow-0;
    border: 1px solid $yellow-50;

    svg {
      fill: $yellow-250;
    }
  }

  &.blue {
    background-color: var(--primary-0);
    border: 1px solid var(--primary-100);

    svg {
      fill: var(--primary-250);
    }
  }

  &.gray {
    background-color: $neutral-50;
    border: 1px solid $neutral-150;

    svg {
      fill: $neutral-250;
    }
  }
}
</style>
